import { graphql, PageProps } from 'gatsby';
import { Link, useTranslation } from 'gatsby-plugin-react-i18next';
import { useCallback } from 'react';
import { helmetJsonLdProp } from 'react-schemaorg';
import { Product } from 'schema-dts';

import { Accordion } from '@/components/Accordion';
import { BackNavigation } from '@/components/BackNavigation';
import { Layout } from '@/components/Layout';
import { Seo } from '@/components/Seo';
import { Gallery, GalleryLayout, GalleryProps } from '@/components/Gallery';
import { ProductInfoSection } from '@/components/ProductInfoSection';
import { ProductPresentation } from '@/components/ProductPresentation';
import { ProductProcessing } from '@/components/ProductProcessing';
import { ProductVideos } from '@/components/ProductVideos';

const GALLERY_OPTIONS: GalleryProps['options'] = {
  initialIndex: 2,
  wrapAround: true,
  pageDots: false,
  adaptiveHeight: false,
  prevNextButtons: true,
};

const ProductPage = ({ data }: PageProps<Queries.ProductTemplateQuery>) => {
  const { t } = useTranslation();

  const getInfoSections = useCallback(() => {
    if (!data?.product?.info_sections) {
      return [];
    }

    return data.product.info_sections.map((section) => ({
      component: <ProductInfoSection sections={section!.sections} />,
      id: section!.id!,
      title: section!.title!,
    }));
  }, [data]);

  if (!data?.product || !data?.category) {
    return null;
  }

  const colorScheme =
    data.category.background === 'light' ? 'color-scheme3' : 'color-scheme2';

  return (
    <Layout data={data}>
      <Seo
        description={data.product.seo?.metaDescription ?? data.product.subtitle}
        title={data.product.seo?.metaTitle ?? data.product.title!}
        bodyAttributes={{
          class: colorScheme,
        }}
        script={[
          helmetJsonLdProp<Product>({
            '@context': 'https://schema.org',
            '@type': 'Product',
            'name': data.product.title!,
            'description': data.product.subtitle ?? undefined,
            'image':
              data.product.featured_image?.localFile?.publicURL ?? undefined,
            'offers': [],
          }),
        ]}
      />

      <div className={'template-product'}>
        <BackNavigation
          link={`/categories/${data.category!.slug}`}
          title={data.product.title!}
        />

        <div className='content'>
          {data.product.solutions && data.product.solutions.length > 0 && (
            <Gallery
              title={t('PHOTO_GALLERY')}
              data={data.product.solutions.map((solution) => ({
                image: solution!.featured_image?.localFile?.publicURL!,
              }))}
              layout={GalleryLayout.B}
              options={GALLERY_OPTIONS}
            />
          )}
          <div className='grid'>
            <div />
            <h2>{data.product.subtitle}</h2>
            <div className='image-wrapper'>
              <div>
                {data.product.featured_image && (
                  <img
                    alt={`${data.product.title} - ${data.product.subtitle}`}
                    src={data.product.featured_image.localFile!.publicURL!}
                  />
                )}

                <Link to={'/contacts'}>{t('REQUEST_INFORMATION')}</Link>
              </div>
            </div>
            <div>
              <Accordion
                className={colorScheme}
                items={[
                  {
                    id: t('PRESENTATION'),
                    title: t('PRESENTATION'),
                    component: (
                      <ProductPresentation
                        presentation={data.product.presentation}
                      />
                    ),
                  },
                  {
                    component: (
                      <ProductProcessing
                        manufactures={data.product.manufactures}
                      />
                    ),
                    hidden: data.product.manufactures?.length === 0,
                    id: t('PROCESSING'),
                    title: t('PROCESSING'),
                  },
                  {
                    component: <ProductVideos videos={data.product.videos} />,
                    hidden: data.product.videos?.length === 0,
                    id: t('VIDEOS'),
                    title: t('VIDEOS'),
                  },
                  ...getInfoSections(),
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductPage;

export const query = graphql`
  query ProductTemplate(
    $language: String!
    $category: String!
    $slug: String!
  ) {
    ...Layout

    category: strapiProductCategory(slug: { eq: $category }) {
      background
      slug
    }

    product: strapiProduct(slug: { eq: $slug }) {
      ...ProductInfoSectionFragment
      ...ProductManufacturesFragment
      ...ProductPresentationFragment
      ...ProductVideos

      featured_image {
        localFile {
          publicURL
        }
      }
      solutions {
        featured_image {
          localFile {
            publicURL
          }
        }
        id
      }
      seo {
        ...Seo
      }
      subtitle
      title
    }
  }
`;
